import { type FC, useCallback, useEffect, useState } from "react";
import { AiOutlineLoading } from "react-icons/ai";
import MEditor, { type Monaco } from "@monaco-editor/react";
import { debounce } from "lodash";

import { useEditorState } from "@/store";

import { setArduinoLanguage, setPythonEditorTheme } from "./editor_syntax";
import { postMessageToRef } from "@/utils";
import { FS } from "@/singletons/FileSystem";

const LoadingScreen: FC = () => {
  return (
    <div style={{ color: "white" }}>
      <AiOutlineLoading
        style={{ width: "60px", height: "60px", color: "white" }}
      />
      <p>Editor is loading</p>
    </div>
  );
};

const languageMap = {
  cpp: "arduino",
  ino: "arduino",
  py: "python",
};

// Execution tracing: when a scenario's blockly_effects carry TRACE, the Blockly->Python generator
// prefixes every statement block with robot.highlight_block('<id>') so the running block can be lit
// in the workspace. Those lines are plumbing, not the student's program - keep them out of the
// read-only Preview (the file itself, which Godot runs, keeps them).
const TRACE_LINE = /^[ \t]*robot\.highlight_block\(.*\)[ \t]*\r?\n?/gm;   // block ids may contain ) - take the line
const stripTraceLines = (code: string) => code.replace(TRACE_LINE, "");

export const MonacoEditor: FC<{
  initialCode?: string;
  file_path: string;
  setMount: (editor: any) => void;
  readonly?: boolean;
}> = (props) => {
  const { file_path, setMount, initialCode } = props;
  const [userCode, setUserCode] = useState<string | undefined>(undefined);
  const { godotContainerRef } = useEditorState();

  const isDark = document
    .getElementsByTagName("html")[0]
    .classList.contains("dark");

  const editorBeforeMount = (monaco: Monaco) => {
    setArduinoLanguage(monaco);
  };


  useEffect(() => {
    if (!file_path) {
      return
    }
    FS.read(file_path)
      .then((code: string | null) => {
        if (code !== null) {
          setUserCode(props.readonly ? stripTraceLines(code) : code);
        } else {
          setUserCode(initialCode || ""); // Set to initialCode if file read fails
        }
      })
      .catch((_error: any) => {
        setUserCode(initialCode || ""); // Set to initialCode if file read fails
      });

    const writeListenerId = FS.events.registerEventListener("WRITE", async (path: string, content: string) => {
      if (path === file_path && props.readonly) {
        setUserCode(stripTraceLines(content));
      }
    });

    return () => { FS.events.unregisterEventListener("WRITE", writeListenerId) }

  }, []);


  // eslint-disable-next-line react-hooks/exhaustive-deps
  const readyDebouncer = useCallback(
    debounce(() => {
      postMessageToRef(godotContainerRef, {
        action: "setEditorLayoutResponse",
        status: "monacoIsReady",
      });
    }, 500),
    [godotContainerRef]
  );

  const fileExtension = file_path.split(".").pop() || "py";

  return (
    <div className="w-full h-full rounded-b-[30px]">
      <MEditor
        defaultLanguage={
          languageMap[fileExtension as keyof typeof languageMap] || "python"
        }
        language={
          languageMap[fileExtension as keyof typeof languageMap] || "python"
        }
        value={userCode}
        theme={isDark ? "riders-vs-dark" : "arduino-theme"}
        onChange={(value, _event) => {
          setUserCode(value || "");
          FS.write(file_path, value || "");
        }}
        onMount={(editor, _monaco) => {
          setMount(editor);
          readyDebouncer();
        }}
        beforeMount={
          ["cpp", "ino"].includes(fileExtension)
            ? (monaco: Monaco) => editorBeforeMount(monaco)
            : fileExtension === "py"
              ? (monaco: Monaco) => setPythonEditorTheme(monaco)
              : undefined
        }
        className={"rounded-b-[30px]"}
        loading={<LoadingScreen />}
        options={{
          readOnly: props.readonly,
        }}
      />
    </div>
  );
};
